import type { TranscriptTurn } from '@acs/shared';
import { auth } from './firebase.js';

/**
 * Client for the voice-gateway's browser test-call endpoints. This is a
 * `shared/api/` module (ADR-0003), so it — not the feature component — is the
 * one that touches Firebase (for the caller's ID token).
 */
const GATEWAY_URL = ((import.meta.env.VITE_GATEWAY_URL as string | undefined) ?? '').replace(/\/$/, '');

async function authedPost<T>(path: string, body: unknown): Promise<T> {
  if (!GATEWAY_URL) throw new Error('VITE_GATEWAY_URL is not configured');
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in.');
  const idToken = await user.getIdToken();
  const resp = await fetch(`${GATEWAY_URL}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`${path} failed (${resp.status}) ${detail}`.trim());
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

export interface TestSessionStart {
  /** WebRTC answer SDP — the gateway created the provider call (doc 33 W10 #5). Empty on Live. */
  sdp: string;
  /** Live engine: the gateway media socket to stream to, as a phone carrier
   * does — the call then runs the phone's own session (2026-10-15). */
  mediaUrl?: string;
  sessionId: string;
  businessId: string;
  model: string;
  /** A deliberate difference from the phone path, labelled; null when none. */
  experiment: string | null;
  greetingToolChoice: 'none' | 'auto';
  /** The server hangs the session up at this point; the browser timer is a courtesy. */
  maxSeconds: number;
  deadlineAt: number;
  policyVersion: string;
  /** The engine this call runs — the same one every phone call runs. */
  engine?: { kind: 'realtime' | 'live'; bookingConfirmation: 'before' | 'after' };
  /** The SAME response policy the phone gateway runs. */
  policy: {
    reprompt: string;
    closingNudge: string;
    silenceMs: number;
    farewellMs: number;
    maxRetries: number;
    maxCallerWaitMs: number;
    /** Rate-limit wait budget (doc 38); absent from an older gateway. */
    rateLimitWaitMs?: number;
    stallMs: number;
    /** Optional for a gateway older than the first-output watchdog. */
    firstOutputMs?: number;
  };
}

/**
 * Start a test (or demo) call. The gateway admits the session and answers for
 * the engine every call runs on: on Live a media-socket URL (the browser then
 * streams like a phone carrier); on Realtime it creates the provider call from
 * this SDP offer and returns the answer. No provider credential — not even an
 * ephemeral one — reaches the browser.
 */
export function startTestSession(input: {
  businessId: string;
  sessionId: string;
  sdp: string;
  from?: string | null;
  demo?: boolean;
}): Promise<TestSessionStart> {
  return authedPost<TestSessionStart>('/test/session', {
    businessId: input.businessId,
    sessionId: input.sessionId,
    sdp: input.sdp,
    from: input.from || undefined,
    // Either transport; the gateway's engine decides which one is used.
    transports: ['webrtc', 'ws'],
    ...(input.demo ? { demo: true } : {}),
  });
}

/** Conversation evidence the server checks a confirmation against: heard
 * assistant speech, committed caller turns, and the caller's words. */
export type TurnEvent =
  | 'assistant_speech'
  | { kind: 'caller_turn'; itemId: string | null }
  | { kind: 'caller_text'; itemId: string | null; text: string };

export interface ExecToolInput {
  businessId: string;
  sessionId: string;
  name: string;
  /** The provider's tool-call id. */
  callId: string;
  args: unknown;
  /** Events since the last tool request, in order (doc 33 W6 #3). */
  events: TurnEvent[];
  /** Setup's "Try it" call: answered from a path that cannot write. */
  demo?: boolean;
}

/** Execute an assistant tool call server-side, through the same executor as a phone call. */
export function execTool(input: ExecToolInput): Promise<unknown> {
  return authedPost<unknown>('/test/tool', input);
}

export interface FinalizeTestCallInput {
  businessId: string;
  sessionId: string;
  transcript: TranscriptTurn[];
  error: string | null;
}

/**
 * Persist a finished test call. Only what the browser alone has is sent — the
 * transcript and a transport error. The outcome, the appointments and the
 * message are the server's (doc 33 W10).
 */
export function finalizeTestCall(input: FinalizeTestCallInput): Promise<void> {
  return authedPost<void>('/test/finalize', input);
}

export interface FinalizeDemoCallInput {
  businessId: string;
  /** So the gateway can end the provider session it created. */
  sessionId: string;
  /** Epoch ms when the session opened. */
  startedAt: number;
  endedBy: 'user' | 'timeout' | 'error';
  transcript: TranscriptTurn[];
}

/**
 * Store a finished setup demo call for analytics (doc 13a §6a).
 *
 * A different endpoint from {@link finalizeTestCall} because it writes
 * somewhere else entirely: a top-level collection, never the business's own
 * call history. Nothing here reaches the owner's dashboard.
 */
export function finalizeDemoCall(input: FinalizeDemoCallInput): Promise<void> {
  return authedPost<void>('/test/demo-finalize', input);
}
